import React from "react";
import { formatCurrency, formatShortDate } from "../../../utils/format";
import EmptyValue from "../../../components/EmptyValue";
import StatusSelect from "../../../components/StatusSelect";
import RowActions from "../../../components/RowActions";
import { PRESORTED } from "../../../../utils/constants";

// Orders table columns.
//
// The prototype has 33 data columns plus a checkbox (its own changelog says 29 — the
// code disagrees with the document; the code is what ops actually sees). Six are frozen.
//
// A column is defined here only when this backend can actually fill it. The prototype's
// demo data fabricates a lot of what it displays — `packedBy`, `dispatchTime`,
// `codPod`, `medType`, `patientAge`, `doctorName` and others are generated by index
// arithmetic in ORDER_SEED (`packedBy` is literally "Rina"/"Nitin" cycling, `doctorName`
// is the same "Dr. Ashwin Rao" on every row). Rendering an always-empty column for each
// of those would be a worse dashboard than omitting them, so the ones with no source are
// listed in UNAVAILABLE_COLUMNS below and surfaced to the user as a note rather than
// silently dropped.
//
// Two cells are controls rather than readouts, matching the legacy screens: "Order Status"
// is an inline <select> that writes on change (StatusSelect), and the last column is
// legacy's Action cell (RowActions — edit, plus delete when the row has no live payment).
// Both are marked `excludeFromExport`/given an `exportValue` so the Excel download below
// still gets data rather than JSX.
//
// Cell styling is the legacy PillEngine vocabulary, not the `--ops-*` tokens this file
// was originally written against: a clickable cell is legacy's ID/Name column treatment
// (`font-sans font-medium text-elm-500 underline hover:text-black`, from
// orderTable/table-layout.jsx:313), a placeholder is `text-gray-400 text-sm` (via
// EmptyValue), and the row checkbox is legacy's own checkbox string verbatim from
// orderTable/table-layout.jsx:202,336.

/**
 * Legacy's row-checkbox class string, copied verbatim from
 * `src/components/orderTable/table-layout.jsx:202` (select-all) and `:336` (per row).
 * The blue check comes from @tailwindcss/forms reading `text-blue-600`, so this replaces
 * the old `accent-*` utility rather than sitting alongside it. The `dark:` variants are
 * carried over as-is: Tailwind's default `darkMode: "media"` is in force here exactly as
 * it is on /all-orders, so dropping them would make these checkboxes the odd ones out.
 */
const CHECKBOX_CLASS =
    "w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600";

/**
 * Legacy's clickable-cell treatment. `underline` + `hover:text-black` is what every
 * link-like cell on /all-orders and /quotations does; the v2 original had the inverse
 * (`hover:underline`), which reads as a different table.
 */
const LINK_CLASS = "font-sans font-medium text-elm-500 underline hover:text-black cursor-pointer";

/**
 * Columns the prototype shows that this backend cannot fill. Surfaced in the UI as an
 * explicit note — an ops user who has seen the prototype will look for these, and
 * "not collected" is a better answer than a column of blanks.
 */
export const UNAVAILABLE_COLUMNS = [
    { label: "Packed By", reason: "Not recorded — no packer is captured against an order" },
    { label: "Dispatch Time", reason: "Not recorded — no dispatch timestamp is written" },
    { label: "Med Type", reason: "Not stored on the order" },
    { label: "COD/POD", reason: "Derivable from payment method once payments exist on the order" },
    { label: "Substitution Reason", reason: "Not stored on the order" },
];

/**
 * Same date text the cell shows, but blank rather than the em-dash placeholder when there
 * is no date — a spreadsheet wants an empty cell there, not a dash to filter around.
 */
function exportDate(value) {
    return value ? formatShortDate(value) : "";
}

/**
 * Money already collected against the order. Used by two money columns and by the Excel
 * export, so it lives here rather than being inlined three times.
 *
 * `SUCCESS` and `PAID` are both counted because the payments table carries both spellings
 * depending on the gateway that wrote the row — that pairing predates this change and is
 * kept verbatim.
 *
 * Exported so `src/v2/ops/pages/orders-legacy` can reuse it against the same flattened row
 * shape (`toOrderRow`) rather than reimplementing the SUCCESS/PAID pairing a second time.
 */
export function paidTotal(row) {
    return (row.payments || [])
        .filter((payment) => ["SUCCESS", "PAID"].includes(payment.status))
        .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
}

/**
 * SAFETY GUARD — legacy's delete precondition, copied from
 * `src/components/quotationsTable/table-instance.jsx:30`: the delete button is rendered
 * only when the row has NO payment in `SUCCESS` or `PENDING`. Deleting an order that has
 * taken (or is about to take) money is the one genuinely irreversible mistake available
 * on this screen, so the button must not exist for those rows — see the note in
 * `src/v2/components/RowActions.jsx`, which deliberately leaves this decision to the
 * caller.
 *
 * `row.payments` is the flattened list from `toOrderRow`; `row.raw.payments` is the same
 * array straight off the wire and is read as a fallback so a row shape that skipped the
 * flattener can never be mistaken for "no payments" (i.e. fail closed, not open).
 *
 * @param {object} row
 * @returns {boolean} true only when nothing is owed or paid on this order.
 */
export function canDeleteOrder(row) {
    const payments = row.payments || row.raw?.payments || [];
    return payments.filter((payment) => payment.status === "SUCCESS" || payment.status === "PENDING").length === 0;
}

/**
 * Build the `{ headers, body }` pair `DownLoadTable` wants from the very columns the
 * table is rendering, so the sheet can never drift from the screen.
 *
 * Two columns are skipped: the row checkbox and Action, which are controls rather than
 * data. Both are marked `excludeFromExport` on their definitions; the `typeof label`
 * test is a second net that also catches any future column whose header is JSX (a JSX
 * header cannot be written into a spreadsheet cell).
 *
 * A column exports `col.exportValue(row)` when it has one — needed wherever `render`
 * returns JSX — and otherwise the raw `row[col.key]`, which is exactly what the plain
 * columns display.
 *
 * @param {Array} columns - the same array passed to DataTable.
 * @param {Array} rows - the rows currently rendered.
 * @returns {{ headers: string[], body: Array<Array<string|number>> }}
 */
export function buildExcelPayload(columns, rows) {
    const exportable = columns.filter((col) => !col.excludeFromExport && typeof col.label === "string");

    return {
        headers: exportable.map((col) => col.label),
        body: (rows || []).map((row) =>
            exportable.map((col) => {
                const value = col.exportValue ? col.exportValue(row) : row[col.key];
                // `null`/`undefined` become an empty cell rather than the string "null".
                return value === null || value === undefined ? "" : value;
            })
        ),
    };
}

/**
 * @param {object} handlers
 * @param {(row) => void} handlers.onOpenPayment
 * @param {(row) => void} handlers.onOpenDelivery
 * @param {(row) => void} handlers.onOpenProfile
 * @param {(row) => void} handlers.onOpenBill
 * @param {Set<string>} handlers.selectedIds
 * @param {(id) => void} handlers.onToggleRow
 * @param {() => void} handlers.onToggleAll
 * @param {boolean} handlers.allSelected
 * @param {string[]} handlers.presortedStatuses - allowed statuses for PRESORTED orders.
 * @param {string[]} handlers.stripsStatuses - allowed statuses for STRIPS orders.
 * @param {(row, nextStatus: string) => void} handlers.onChangeStatus
 * @param {string|number|null} handlers.savingOrderId - the row whose status write is in flight.
 * @param {(row) => void} handlers.onEditOrder
 * @param {(row) => void} handlers.onDeleteOrder
 */
export function buildOrderColumns({
    onOpenPayment,
    onOpenDelivery,
    onOpenProfile,
    onOpenBill,
    selectedIds,
    onToggleRow,
    onToggleAll,
    allSelected,
    presortedStatuses = [],
    stripsStatuses = [],
    onChangeStatus,
    savingOrderId = null,
    onEditOrder,
    onDeleteOrder,
}) {
    const stop = (event) => event.stopPropagation();

    /**
     * Which allowed-status list applies to a row. Mirrors legacy's ALL_ORDER_TYPE branch
     * (`src/components/orderTable/table-instance.jsx:337`): the type string is tested for
     * `PRESORTED`, and everything else falls back to the strips list.
     *
     * The lists themselves are fetched once, at the top level of the page component —
     * `getOrderStatuses()` is a hook in disguise and must never be called from in here,
     * which runs during a cell render.
     */
    const statusOptionsFor = (row) => {
        const type = row.orderType || row.raw?.type || "";
        return (type.includes(PRESORTED) ? presortedStatuses : stripsStatuses) || [];
    };

    return [
        {
            key: "select",
            width: 44,
            // A control, not data — never written into the Excel export.
            excludeFromExport: true,
            label: (
                <input
                    type="checkbox"
                    aria-label="Select all orders on this page"
                    checked={allSelected}
                    onChange={onToggleAll}
                    className={CHECKBOX_CLASS}
                />
            ),
            render: (row) => (
                <input
                    type="checkbox"
                    aria-label={`Select order ${row.orderId}`}
                    checked={selectedIds.has(String(row.orderId))}
                    onClick={stop}
                    onChange={() => onToggleRow(String(row.orderId))}
                    className={CHECKBOX_CLASS}
                />
            ),
        },
        // The prototype's MONTH column (e.g. "Aug") occupied one of the six frozen
        // columns — the scarcest space on the screen — for information Order Date
        // already carries. Dropped rather than ported.
        {
            key: "orderDate",
            width: 130,
            label: "Order Date",
            render: (row) => formatShortDate(row.orderDate),
            exportValue: (row) => exportDate(row.orderDate),
        },
        {
            key: "orderId",
            width: 120,
            label: "Order ID",
            render: (row) => (
                // Legacy's ID column, minus `underline`/`cursor-pointer` — this id is
                // not clickable and must not claim to be. Monospace is gone on purpose:
                // legacy has none, and `font-mono` emits no CSS in this app.
                <span className="whitespace-nowrap font-sans font-medium text-elm-500">{row.orderId}</span>
            ),
        },
        {
            key: "patientName",
            width: 150,
            label: "Patient Name",
            render: (row) => (
                <button
                    type="button"
                    onClick={(event) => {
                        stop(event);
                        onOpenProfile(row);
                    }}
                    className={`text-left ${LINK_CLASS}`}
                >
                    {row.patientName || <EmptyValue />}
                </button>
            ),
        },
        { key: "contactNumber", width: 130, label: "Contact" },

        // --- end of frozen columns -----------------------------------------
        { key: "caregiverName", label: "Caregiver" },
        {
            key: "address",
            label: "Complete Address",
            wrap: true,
            render: (row) => {
                const joined = [row.address, row.city, row.state, row.pincode].filter(Boolean).join(", ");
                return joined || <EmptyValue />;
            },
            exportValue: (row) => [row.address, row.city, row.state, row.pincode].filter(Boolean).join(", "),
        },
        { key: "city", label: "City" },
        { key: "state", label: "State" },
        { key: "pincode", label: "Pincode" },
        { key: "duration", label: "Duration", render: (row) => (row.duration ? `${row.duration} days` : null) },
        { key: "orderType", label: "Order Type" },
        {
            key: "startDate",
            label: "Start Date",
            render: (row) => formatShortDate(row.startDate),
            exportValue: (row) => exportDate(row.startDate),
        },
        {
            key: "endDate",
            label: "End Date",
            render: (row) => formatShortDate(row.endDate),
            exportValue: (row) => exportDate(row.endDate),
        },
        { key: "channel", label: "Order Channel" },
        { key: "source", label: "Source" },
        { key: "inventoryId", label: "Invoice No. (Evital)" },
        {
            key: "status",
            label: "Order Status",
            // Was a read-only StatusBadge. Legacy lets ops change a status straight from
            // the row (`table-instance.jsx:295/316/337`) and this screen is meant to carry
            // the same affordances, so the badge is replaced by the legacy inline <select>.
            //
            // The status codes are shown raw (ORDER_PENDING, …) rather than through
            // `getStatusMeta`'s prettier labels, because these are the values being WRITTEN
            // — a dropdown that displays "Order Pending" but stores `ORDER_PENDING` invites
            // exactly the mismatch the unknown-value guard inside StatusSelect exists to
            // catch, and legacy shows the codes for the same reason.
            //
            // KNOWN GAP, deliberate: legacy's own order-status cell intercepts a change to
            // ORDER_CANCELLED and holds it until a cancellation reason is collected
            // (`OrderStatusCell` + CancellationReasonPrompt). This select writes `{ status }`
            // only, so cancelling from here stores no reason. Wiring that prompt in is a
            // separate change — until then, cancel from the legacy /all-orders screen when
            // a reason matters.
            render: (row) => (
                <StatusSelect
                    value={row.status}
                    options={statusOptionsFor(row)}
                    // Disable just the row being written, not the whole column: two ops
                    // users' rows are independent, and a global lock would freeze the table
                    // on every save.
                    disabled={savingOrderId !== null && String(savingOrderId) === String(row.orderId)}
                    onChange={(nextStatus) => onChangeStatus && onChangeStatus(row, nextStatus)}
                />
            ),
            exportValue: (row) => row.status,
        },
        {
            key: "drxView",
            label: "DRX View",
            render: (row) => (
                <a
                    href={`/drx/${row.orderId}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={stop}
                    className={LINK_CLASS}
                >
                    Open
                </a>
            ),
            // The cell is a link labelled "Open"; the sheet gets the path it points at.
            exportValue: (row) => `/drx/${row.orderId}`,
        },
        {
            key: "total",
            label: "Invoice Amount",
            align: "right",
            render: (row) => (
                <button
                    type="button"
                    onClick={(event) => {
                        stop(event);
                        onOpenPayment(row, "invoice");
                    }}
                    className={`tabular-nums ${LINK_CLASS}`}
                >
                    {formatCurrency(row.total)}
                </button>
            ),
            // Numbers, not "₹1,23,457": a spreadsheet column of formatted strings cannot
            // be summed.
            exportValue: (row) => (row.total === null || row.total === undefined ? "" : Number(row.total)),
        },
        {
            key: "amountReceived",
            label: "Amount Received",
            align: "right",
            render: (row) => (
                <button
                    type="button"
                    onClick={(event) => {
                        stop(event);
                        onOpenPayment(row, "received");
                    }}
                    className={`tabular-nums ${LINK_CLASS}`}
                >
                    {formatCurrency(paidTotal(row))}
                </button>
            ),
            exportValue: (row) => paidTotal(row),
        },
        {
            key: "outstanding",
            label: "Refund / Pending",
            align: "right",
            render: (row) => {
                const outstanding = (Number(row.total) || 0) - paidTotal(row);
                // Plain legacy body text — this cell is not clickable. `tabular-nums` is
                // the legacy-compatible way to keep a right-aligned money column's digits
                // in line now that the monospace token is gone (font-mono emits nothing).
                return outstanding > 0 ? (
                    <span className="tabular-nums font-sans text-black">{formatCurrency(outstanding)}</span>
                ) : (
                    <EmptyValue />
                );
            },
            exportValue: (row) => {
                const outstanding = (Number(row.total) || 0) - paidTotal(row);
                // Blank where the cell shows a placeholder, so the sheet says "nothing
                // outstanding" the same way the screen does.
                return outstanding > 0 ? outstanding : "";
            },
        },
        { key: "notes", label: "Notes", wrap: true },
        { key: "partnerName", label: "Partner" },
        {
            key: "evitalBill",
            label: "Bill",
            render: (row) => (
                <button
                    type="button"
                    onClick={(event) => {
                        stop(event);
                        onOpenBill(row);
                    }}
                    className={LINK_CLASS}
                >
                    View
                </button>
            ),
            // The cell is a modal trigger; the sheet gets the datum the modal is about.
            exportValue: (row) => row.inventoryId,
        },
        {
            key: "deliveryDetails",
            label: "Delivery Details",
            render: (row) => (
                <button
                    type="button"
                    onClick={(event) => {
                        stop(event);
                        onOpenDelivery(row);
                    }}
                    disabled={!row.delivery}
                    className={`${LINK_CLASS} disabled:cursor-not-allowed disabled:text-gray-400 disabled:text-sm disabled:no-underline disabled:hover:text-gray-400`}
                >
                    {row.delivery ? "View" : "Not shipped"}
                </button>
            ),
            exportValue: (row) => (row.delivery ? row.delivery.status || "" : "Not shipped"),
        },
        {
            // Legacy's Action cell, last column, exactly as on /quotations and /all-orders.
            // A control, so it is excluded from the Excel export.
            key: "actions",
            label: "Action",
            excludeFromExport: true,
            render: (row) => (
                <RowActions
                    // See canDeleteOrder above: no SUCCESS/PENDING payment, or no delete
                    // button at all. RowActions leaves this judgement to the caller.
                    canDelete={canDeleteOrder(row)}
                    onEdit={() => onEditOrder && onEditOrder(row)}
                    onDelete={() => onDeleteOrder && onDeleteOrder(row)}
                />
            ),
        },
    ];
}
